/**
 * 360 联运 JSSDK 封装
 * 文档：【4-对接文档JS版】360联运JSSDK集成说明 V2.0.0.2
 *
 * SDK 本体（SDK360.js / index.js）从 http://softdl.360tpcdn.com/pcrj/JSSDK360.zip 下载，
 * 随产品打包，在 index.html 中通过 <script src="/SDK360.js"></script> 引入，挂载到 window.SDK360。
 * SDK 支持系统：win7 及以上。
 */

// SDK360 实例挂载在 window 上
const win = window as any;

/**
 * SDK 本地错误专用 errno（非 360 服务端业务码）。
 * SDK360.js 内部 `function l(t){return {errno:110001, errmsg:t, data:""}}` 只在两种情况产生：
 *   1. 未调用 init（errmsg 为开发者向文案「请先调用init方法进行初始化！」）
 *   2. 入参校验失败 / 内部异常（errmsg 是 Error 对象，不是字符串）
 * 两者都不可直接展示给用户，统一走 friendlySDK360Error()。
 */
export const SDK360_LOCAL_ERRNO = 110001;

/** 初始化参数（环境信息结构体） */
export interface SDK360InitParams {
    appid: string; // 联运产品 ID，不可硬编码，需从自身云端获取
    qid: number;   // 登录联运平台的 360 账号 ID
    mid: string;   // 由 C++ 读注册表获取，取不到传空串（非 null）
    m2: string;    // 由 C++ 读注册表获取，取不到传空串（非 null）
}

/** createOrder 入参（与后端 /workai/pay/360/order 返回字段一一对应） */
export interface SDK360OrderData {
    order_id: string;       // 厂商订单 id，应用内唯一（由后端采番）
    order_amount: number;   // 订单金额，单位“分”
    create_time: string;    // 创建时间戳（10 位秒）
    user_id: string;        // 用户 ID
    product_id: string;     // 商品 id
    product_name: string;   // 商品描述
    // 以下三个字段文档标注为“开启代扣/任务单后必填”，但官方 demo 无论何种场景都会传。
    // 缺失时服务端 /main/sdk/v1/order/gen 可能返回 errno 10017「不支持的接入模式」，故按 demo 对齐必传。
    order_pay_type: number; // 订单类型：0-付费单 3-任务单
    auto_pay_status: number;// 是否开启代扣：0-不开启 1-开启
    task_id: string;        // 任务 ID，付费单传空串
}

/**
 * createOrder responseCallback 返回结构。
 * 注意：业务数据只包一层（SDK 内部读的是 res.data.order_id），与 statusCallback 不同。
 */
export interface SDK360OrderResponse {
    errno: number;
    /**
     * 错误描述。注意：errno 为 SDK_LOCAL_ERRNO(110001) 时这里可能是 Error 对象而非字符串
     * （SDK 内部 catch 后原样塞进来），且文案是面向开发者的，不可直接展示给用户。
     * 统一用 friendlySDK360Error() 转换。
     */
    errmsg: string;
    time?: number;
    type?: number;
    data?: {
        /**
         * 360 侧订单 id（非厂商订单号）。
         * SDK 用它作为内部轮询表 queryList 的 key，cancelPay 必须传这个值，
         * 传厂商订单号会静默失效（cancelPay 无论如何都返回 0，看不出失败）。
         */
        order_id: string | number;
        qr_code: string;        // 订单二维码内容（字符串，需前端自行生成二维码图片）
        order_amount?: number;
        expire_time?: string;   // 过期日期
        remainder?: string;     // expire_time 的相对时间，单位秒
    } | "";                     // errno 非 0 时为空串
}

/**
 * createOrder statusCallback 返回结构。
 * 注意：文档 3.2 把字段画成平铺的，实际 SDK 多包一层 —— 业务数据在 res.data.data，
 * 轮询间隔在 res.data.control.polling_interval。
 */
export interface SDK360OrderStatus {
    errno: number;          // 0-正常，非 0-异常
    errmsg: string;
    time?: number;
    type?: number;
    data?: {
        control?: {
            polling_interval?: number;  // 轮询间隔，单位秒（SDK 内部使用，无需前端处理）
        };
        data?: {
            order_id?: number;      // 360 的订单 id
            /**
             * 订单状态：
             * 10-待付款(初始) 20-付款完成(待通知厂商) 30-待厂商发权益(已通知厂商)
             * 40-售后中 50-交易完成 60-已取消 70-交易关闭
             * 注：20/30/50 均视为支付成功。SDK 内部轮询，>10 时停止轮询并回调。
             */
            order_status?: number;
            pay_channel?: number;   // 1-微信 2-支付宝
        };
    };
}

/** SDK 文件是否已加载（window.SDK360 存在） */
export function isSDK360Ready(): boolean {
    return !!win.SDK360;
}

/**
 * SDK 是否已成功初始化（appid / qid 已赋值）。
 * 对应 SDK 内部 `checkIsInit(){ return this.appid && this.qid }` —— 注意这与 isSDK360Ready()
 * 是两件事：SDK 文件加载成功但 init 失败（例如 get360Config 请求失败）时，
 * 后续任何接口都会返回 errno 110001「请先调用init方法进行初始化！」。
 */
export function isSDK360Inited(): boolean {
    return !!(isSDK360Ready() && win.SDK360.appid && win.SDK360.qid);
}

/**
 * 把 SDK / 360 服务端返回的错误转成可直接展示给用户的文案。
 *
 * SDK360.js 是第三方文件不可改动，它的错误文案（如「请先调用init方法进行初始化！」）是开发者向的，
 * 甚至可能是 Error 对象，直接 toast 会把内部实现暴露给用户。这里统一收口：
 *   - errno 110001（SDK 本地错误）→ 一律用兜底文案，原始信息只进 console
 *   - errmsg 不是字符串（Error 对象）→ 同样兜底
 *   - 其余（360 服务端业务错误，如余额不足）→ 沿用服务端文案，对用户有意义
 *
 * @param res SDK 回调返回对象
 * @param fallback 兜底文案
 */
export function friendlySDK360Error(
    res: { errno?: number; errmsg?: any } | null | undefined,
    fallback = "支付服务暂时不可用，请稍后重试"
): string {
    const raw = res?.errmsg;

    // SDK 本地错误：文案面向开发者（未初始化、参数校验失败等），不可外露
    if (res?.errno === SDK360_LOCAL_ERRNO) {
        console.error("[SDK360] SDK 内部错误（已对用户屏蔽）", raw);
        return fallback;
    }

    // errmsg 可能是 Error 对象（SDK 内部 catch 后原样回传），toast 出来会是 [object Error]
    if (typeof raw !== "string" || !raw.trim()) {
        if (raw) console.error("[SDK360] 非字符串错误信息（已对用户屏蔽）", raw);
        return fallback;
    }

    return raw;
}

/**
 * 初始化 SDK（软件启动时必须调用）
 */
export function initSDK360(params: SDK360InitParams): void {
    if (!isSDK360Ready()) {
        console.error("[SDK360] SDK 未加载，无法初始化");
        return;
    }
    win.SDK360.init(params);
    console.log("[SDK360] init", params);
}

/**
 * 创建支付订单
 * @param data 订单数据（来自后端 /workai/pay/360/order）
 * @param responseCallback 创建订单结果回调（含 qr_code）
 * @param statusCallback 订单状态回调（SDK 内部轮询驱动）
 */
export function createOrder360(
    data: SDK360OrderData,
    responseCallback: (res: SDK360OrderResponse) => void,
    statusCallback: (res: SDK360OrderStatus) => void
): void {
    if (!isSDK360Ready()) {
        console.error("[SDK360] SDK 未加载，无法创建订单");
        return;
    }
    win.SDK360.createOrder(data, responseCallback, statusCallback);
}

/**
 * 主动查询订单状态（用于「我已完成支付」按钮）。
 *
 * 注意：这与 createOrder 内部轮询是同一个函数——
 * - 只在 order_status > 10（已支付/终态）时才回调；仍是 10（待付款）会静默重新调度下一轮。
 * - 与 createOrder 已启动的内部轮询共用 queryList[order_id] 槽位，直接叠加调用会导致孤儿定时器
 *   指数增长。故调用前务必先 cancelPay360(order_id) 收敛成单条轮询链（见 useSDK360Payment.queryNow）。
 *
 * @param params { order_id: 360 侧订单 id（数字）, user_id }
 * @param callback 与 statusCallback 同结构（业务数据在 res.data.data）
 */
export function queryOrderStatus360(
    params: { order_id: number; user_id: string },
    callback: (res: SDK360OrderStatus) => void
): void {
    if (!isSDK360Ready()) {
        console.error("[SDK360] SDK 未加载，无法查询订单状态");
        return;
    }
    win.SDK360.getOrderStatus(params, callback);
}

/**
 * 取消订单 / 停止底层轮询（页面离开或刷新二维码时调用）
 * @param order_id 创建订单成功时返回的 order_id
 * @return 0-取消成功，其它值-取消失败
 */
export function cancelPay360(order_id: string | number): number {
    if (!isSDK360Ready()) return -1;
    return win.SDK360.cancelPay(order_id);
}
